import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@atoms/Card';
import { Badge } from '@atoms/Badge';
import { Button } from '@atoms/Button';
import { Input } from '@atoms/Input';
import { Spinner } from '@atoms/Spinner';
import { adminService } from '@services/adminService';
import { getErrorMessage } from '@services/api';
import { formatDate } from '@utils/formatters';

export function WhatsAppSection() {
  const queryClient = useQueryClient();
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('Lezzet Yolda Toptan test mesajı');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(
    null,
  );

  const { data, isLoading, refetch, isError: statusError } = useQuery({
    queryKey: ['admin', 'whatsapp'],
    queryFn: () => adminService.getWhatsAppStatus(),
    refetchInterval: (query) => (query.state.data?.connected ? 15000 : 5000),
  });

  const needsQr = !data?.connected;

  const { data: qr, refetch: refetchQr, isFetching: qrLoading } = useQuery({
    queryKey: ['admin', 'whatsapp-qr'],
    queryFn: () => adminService.getWhatsAppQr(),
    refetchInterval: needsQr ? 4000 : false,
    enabled: needsQr,
  });

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ['admin', 'notification-logs'],
    queryFn: () => adminService.getNotificationLogs(20),
  });

  const testMutation = useMutation({
    mutationFn: () => adminService.sendTestWhatsApp(testPhone, testMessage),
    onSuccess: (result) => {
      if (result.status === 'sent') {
        setFeedback({ type: 'success', text: 'Test mesajı gönderildi.' });
      } else {
        setFeedback({
          type: 'error',
          text:
            result.errorMessage ??
            'Mesaj gönderilemedi. WhatsApp bağlantısını ve telefon numarasını kontrol edin.',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'notification-logs'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'whatsapp'] });
    },
    onError: (err) => {
      setFeedback({ type: 'error', text: getErrorMessage(err, 'Test mesajı gönderilemedi.') });
    },
  });

  const qrRefreshMutation = useMutation({
    mutationFn: () => adminService.refreshWhatsAppQr(),
    onSuccess: (result) => {
      if (result.connected) {
        setFeedback({ type: 'success', text: 'WhatsApp zaten bağlı, QR gerekmiyor.' });
      } else if (result.refreshed) {
        setFeedback({
          type: 'info',
          text: 'Yeni QR kod isteniyor — birkaç saniye içinde otomatik görünecek.',
        });
      } else {
        setFeedback({
          type: 'error',
          text: result.reason ?? 'QR yenilenemedi. Köprü servisini kontrol edin.',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'whatsapp'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'whatsapp-qr'] });
    },
    onError: (err) => {
      setFeedback({ type: 'error', text: getErrorMessage(err, 'QR yenilenemedi.') });
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => adminService.resetWhatsApp(),
    onSuccess: (result) => {
      if (result.reset) {
        setFeedback({
          type: 'success',
          text: 'WhatsApp oturumu sıfırlandı. Birkaç saniye içinde QR kodu görünecek — telefonunuzdan tarayın.',
        });
      } else {
        setFeedback({
          type: 'error',
          text: result.reason ?? 'Oturum sıfırlanamadı. Köprü servisinin çalıştığından emin olun (port 7799).',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['admin', 'whatsapp'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'whatsapp-qr'] });
      setTimeout(() => {
        void refetch();
        void refetchQr();
      }, 1500);
    },
    onError: (err) => {
      setFeedback({ type: 'error', text: getErrorMessage(err, 'Oturum sıfırlanamadı.') });
    },
  });

  if (isLoading) return <Spinner />;

  const feedbackClass =
    feedback?.type === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
      : feedback?.type === 'error'
        ? 'border-red-200 bg-red-50 text-red-900'
        : 'border-sky-200 bg-sky-50 text-sky-900';

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">WhatsApp Merkezi</h1>
        <p className="mt-1 text-sm text-slate-500">Bağlantı, test mesajı ve bildirim kayıtları</p>
      </div>

      {feedback && (
        <div className={`rounded-xl border px-4 py-3 text-sm ${feedbackClass}`}>
          {feedback.text}
        </div>
      )}

      {statusError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          WhatsApp durumu alınamadı. API ve köprü servisinin (port 7799) çalıştığını kontrol edin.
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">WhatsApp Hub</h2>
        <button
          type="button"
          onClick={() => {
            void refetch();
            if (needsQr) void refetchQr();
          }}
          className="text-sm text-brand-600 hover:underline"
        >
          Yenile
        </button>
      </div>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <span className="text-4xl">{data?.connected ? '✅' : '❌'}</span>
            <div>
              <p className="font-semibold">
                Durum:{' '}
                <Badge variant={data?.connected ? 'success' : 'danger'}>
                  {data?.connected ? 'Bağlı' : 'Bağlı Değil'}
                </Badge>
              </p>
              {data?.bridgeKind === 'legacy' && (
                <p className="mt-1 text-xs text-amber-700">
                  Eski köprü algılandı. Önerilen: <code className="rounded bg-amber-100 px-1">npm run bridge:real</code>
                </p>
              )}
              {data?.phone && <p className="mt-1 text-sm text-gray-600">Telefon: {data.phone}</p>}
              {needsQr && data?.state === 'AUTHENTICATED' ? (
                <div className="mt-3 flex items-center gap-2 text-sm text-sky-700">
                  <Spinner />
                  <span>Telefon eşleşti, bağlantı kuruluyor…</span>
                </div>
              ) : needsQr ? (
                <div className="mt-3 space-y-2">
                  <p className="text-sm text-amber-600">
                    QR kod taraması gerekli. Telefonunuzda WhatsApp → Bağlı cihazlar → Cihaz bağla.
                  </p>
                  {qr?.qrData ? (
                    qr.qrData.startsWith('data:') || qr.qrData.startsWith('http') ? (
                      <img src={qr.qrData} alt="WhatsApp QR" className="h-52 w-52 rounded-lg border" />
                    ) : (
                      <div className="rounded-lg border bg-white p-4 text-center">
                        <p className="mb-2 text-xs text-gray-500">Stub mod — gerçek QR için bridge:real kullanın</p>
                        <p className="break-all font-mono text-sm">{qr.qrData}</p>
                      </div>
                    )
                  ) : data?.state === 'INITIALIZING' || data?.state === 'RESTARTING' || qrLoading ? (
                    <p className="text-xs text-gray-500">
                      QR hazırlanıyor… (ilk açılışta 30–60 sn sürebilir, otomatik görünecek)
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      QR henüz üretilmedi. &quot;QR Yenile&quot; ile yeni kod isteyin; düzelmezse
                      &quot;Oturumu Sıfırla&quot; deneyin.
                    </p>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    loading={qrRefreshMutation.isPending}
                    onClick={() => qrRefreshMutation.mutate()}
                  >
                    QR Yenile
                  </Button>
                </div>
              ) : null}
              {data?.lastSeen && (
                <p className="mt-2 text-xs text-gray-500">Son görülme: {formatDate(data.lastSeen)}</p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            loading={resetMutation.isPending}
            onClick={() => resetMutation.mutate()}
          >
            Oturumu Sıfırla
          </Button>
        </div>
      </Card>

      {!data?.connected && (
        <Card className="border-dashed bg-slate-50 text-sm text-slate-600">
          <p className="font-medium text-slate-800">Köprüyü başlatma</p>
          <ol className="mt-2 list-decimal space-y-1 pl-5">
            <li>Eski köprü varsa durdurun: <code className="rounded bg-white px-1">docker compose stop whatsapp-bridge</code></li>
            <li>Gerçek mesaj için: <code className="rounded bg-white px-1">WHATSAPP_BRIDGE_API_KEY=change-me-bridge-api-key npm run bridge:real</code></li>
            <li>Bu sayfayı yenileyin, QR’ı tarayın, ardından test mesajı gönderin.</li>
          </ol>
        </Card>
      )}

      <Card className="space-y-3">
        <h3 className="font-medium">Test Mesajı Gönder</h3>
        <div className="grid gap-3 md:grid-cols-2">
          <Input
            label="Telefon"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="31640400450 veya 905551234567"
          />
          <Input label="Mesaj" value={testMessage} onChange={(e) => setTestMessage(e.target.value)} />
        </div>
        <Button
          loading={testMutation.isPending}
          disabled={!testPhone.trim() || !data?.connected}
          onClick={() => testMutation.mutate()}
        >
          {data?.connected ? 'Test Gönder' : 'Önce WhatsApp Bağlantısı Gerekli'}
        </Button>
      </Card>

      <Card>
        <h3 className="mb-3 font-medium">Son Bildirimler</h3>
        {logsLoading ? (
          <Spinner />
        ) : (logs ?? []).length === 0 ? (
          <p className="text-sm text-gray-500">Henüz kayıt yok.</p>
        ) : (
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {(logs ?? []).map((log) => (
              <div key={log.id} className="rounded-lg border border-gray-100 p-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="font-medium">{log.recipientPhone}</span>
                  <Badge variant={log.status === 'sent' ? 'success' : log.status === 'pending' ? 'warning' : 'danger'}>
                    {log.status}
                  </Badge>
                </div>
                <p className="truncate text-gray-600">{log.message}</p>
                <p className="text-xs text-gray-400">{formatDate(log.createdAt)}</p>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
